<script setup>
import { useAppStore } from '@/stores/App'

const nuxtApp = useNuxtApp()
const app = useAppStore()
const { handle } = useRoute().params
const staticURLImg = useRuntimeConfig().public.staticURLImg
const { getMonthAndYear } = useFormatters()
const updatedHandle = ref()

// Update handle if accessed directly
updatedHandle.value = handle.toLowerCase().replace(/ /g, '-')

const { data, status, error, refresh } = await useLazyFetch('/api/seller/query', {
    query: {
        handle: updatedHandle,
    },
    transform: (data) => {
        // console.log('returned data no value ', data)
        return {
            seller: data,
            fetchedAt: new Date(Date.now()),
        }
    },
    getCachedData(key) {
        const data = nuxtApp.payload.data[key] || nuxtApp.static.data[key]

        if (!data) {
            return
        }

        const expirationDate = new Date(data.fetchedAt)
        expirationDate.setTime(expirationDate.getTime() + 60 * 60000) // cache set to 1 hour
        const isExpired = expirationDate.getTime() < Date.now()
        if (isExpired) {
            return
        }

        return data
    },
})

watchEffect(() => {
    if (error.value) {
        throw createError({
            statusCode: 500,
            message: error.value,
            fatal: false,
        })
    }
})

// console.log('returned data ', data.value?.seller)

if (data.value && data.value?.seller == '404') {
    throw createError({
        statusCode: 404,
        message: 'Seller does not exist',
        fatal: false,
    })
}

// These are for SEO meta
const title = computed(() => {
    return data.value ? data.value?.seller?.displayName + ' profile' : ''
})

const desc = computed(() => {
    return data.value ? data.value?.seller?.bio : ''
})

const ogImg = computed(() => {
    return data.value ? staticURLImg + data.value?.seller?.coverImg : ''
})

// SEO
useSeoMeta({
    title: title,
    ogTitle: title,
    description: desc,
    ogDescription: desc,
    ogImage: ogImg,
    twitterCard: 'summary_large_image',
})
</script>

<template>
    <v-row id="seller" no-gutters class="ma-n4 justify-center fade-down">
        <v-col v-if="status === 'pending' || status === 'idle'" cols="10"><app-loader /></v-col>
        <v-col v-else cols="12">
            <!-- <pre>{{ data }}</pre> -->
            <v-row no-gutters class="mb-16 justify-center">
                <v-col cols="12" class="d-none d-sm-flex pa-0">
                    <v-img
                        :src="
                            data.seller && data.seller.coverImg
                                ? staticURLImg + data.seller.coverImg
                                : staticURLImg + app.noProfileCover
                        "
                        :alt="data.seller?.displayName + `profile cover`"
                    ></v-img>
                </v-col>
                <v-col cols="12" sm="10" class="mt-sm-n16 mb-16">
                    <v-card variant="flat" width="100%" class="st-box-shadow rounded">
                        <v-row no-gutters>
                            <v-col
                                cols="12"
                                sm="4"
                                md="3"
                                lg="2"
                                class="d-flex justify-center align-center bg-grey-darken-3"
                                :class="data.seller && data.seller.profileImg ? 'justify-sm-start' : 'justify-center'"
                            >
                                <v-img
                                    v-if="data.seller && data.seller.profileImg"
                                    :src="staticURLImg + data.seller.profileImg"
                                    :alt="data.seller.displayName + `profile avatar`"
                                ></v-img>
                                <span v-else class="d-none d-sm-flex text-h1">
                                    {{ data.seller.displayName.charAt(0).toUpperCase() }}
                                </span>
                            </v-col>
                            <v-col cols="12" sm="8" md="9" lg="10" class="text-center text-md-left">
                                <v-card-subtitle class="pt-2 text-overline">
                                    {{ data.seller.displayName }}
                                </v-card-subtitle>
                                <v-card-title class="pt-0 pb-0 text-h4 justify-center justify-sm-start">
                                    {{ data.seller.name }}
                                </v-card-title>
                                <v-card-text class="st-subheader pt-0">
                                    {{ data.seller.location }}
                                    <v-icon v-if="data.seller.location" icon="mdi-circle-small"></v-icon>
                                    Member since {{ getMonthAndYear(data.seller.joinedAt) }}
                                </v-card-text>
                                <v-card-text class="pt-0">{{ data.seller.bio }}</v-card-text>
                                <v-card-text class="pb-2 pb-sm-0">
                                    <nuxt-link
                                        :to="data.seller.website"
                                        :aria-label="`Visit ${data.seller.displayName} website`"
                                        target="_blank"
                                        class="st-links"
                                    >
                                        {{ data.seller.website }}
                                    </nuxt-link>
                                </v-card-text>
                            </v-col>
                        </v-row>
                    </v-card>
                </v-col>
                <v-col cols="12" class="mb-16">
                    <lazy-seller-products
                        :seller="data.seller.displayName"
                        :handle="data.seller.handle"
                        :how-many="30"
                    />
                </v-col>
            </v-row>
        </v-col>
    </v-row>
</template>

<style scoped></style>
